import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Clock, User, Share2, Facebook, Twitter, Linkedin, ArrowRight } from 'lucide-react';
import { SEO } from '@/components/SEO';
import { StructuredData } from '@/components/StructuredData';
import { SectionReveal } from '@/components/shared/SectionReveal';

export default function JournalArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [category, setCategory] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadArticle();
  }, [slug]);

  const loadArticle = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/journal/articles/${slug}`);
      const articleData = await response.json();
      setArticle(articleData);

      // Load category
      const categoriesRes = await fetch(`${backendUrl}/api/journal/categories`);
      const categories = await categoriesRes.json();
      const articleCategory = categories.find(c => c.id === articleData.category_id);
      setCategory(articleCategory);

      // Load related articles (same category + manual selection)
      const articlesRes = await fetch(`${backendUrl}/api/journal/articles?status=published&category=${articleData.category_id}`);
      const articles = await articlesRes.json();
      const related = articles
        .filter(a => a.id !== articleData.id)
        .slice(0, 3);
      setRelatedArticles(related);
    } catch (err) {
      console.error('Failed to load article');
    } finally {
      setLoading(false);
    }
  };

  const getCtaForCategory = () => {
    if (!category) return null;

    const ctas = {
      'production-in-mexico': {
        title: 'Planning a Production in Mexico?',
        description: 'Download our comprehensive production guide or request an estimate.',
        link: '/mexico',
        linkText: 'Explore Production Services'
      },
      'craft-technology': {
        title: 'See Our Cinematography Work',
        description: 'Explore selected projects showcasing our craft and vision.',
        link: '/work',
        linkText: 'View Selected Work'
      },
      'behind-the-scenes': {
        title: 'Explore Our Post-Production Services',
        description: 'Color grading, editing, and finishing for premium content.',
        link: '/post',
        linkText: 'Post & Finishing'
      },
      'stories-case-studies': {
        title: 'Ready to Tell Your Story?',
        description: 'Let\'s discuss your next project.',
        link: '/contact',
        linkText: 'Get in Touch'
      },
      'perspectives': {
        title: 'Work with Paco Herrera',
        description: 'Explore director-led production and cinematography.',
        link: '/director',
        linkText: 'About Paco'
      }
    };

    return ctas[category.slug] || ctas['stories-case-studies'];
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = article?.title || '';

  if (loading) return <div className="min-h-screen" />;
  if (!article) return <div className="min-h-screen pt-40 text-center"><p className="text-[hsl(var(--bk-fg-muted))]">Article not found</p></div>;

  const cta = getCtaForCategory();

  return (
    <div className="pt-28 pb-20">
      <SEO
        title={article.seo_title || article.title}
        description={article.seo_description || article.excerpt}
        path={`/journal/${slug}`}
        image={article.og_image || article.hero_image}
        type="article"
        author={article.author || "Paco Herrera"}
        publishedTime={article.published_at}
        modifiedTime={article.updated_at}
        keywords={article.tags && article.tags.length > 0 ? article.tags.join(', ') : null}
      />
      <StructuredData 
        type="article" 
        data={{
          title: article.title,
          description: article.excerpt,
          image: article.hero_image,
          publishedTime: article.published_at,
          modifiedTime: article.updated_at,
          author: article.author || "Paco Herrera"
        }}
      />
      {article.faq_items && article.faq_items.length > 0 && (
        <StructuredData 
          type="faq" 
          data={{ items: article.faq_items }}
        />
      )}

      {/* Hero */}
      <div className="max-w-[1200px] mx-auto px-5 sm:px-8 mb-12">
        <SectionReveal>
          <div className="mb-8">
            <Link to="/journal" className="font-mono-meta text-xs text-[hsl(var(--bk-accent))] hover:opacity-80 transition-opacity">
              ← Back to Journal
            </Link>
          </div>
          
          {category && (
            <span className="inline-block font-mono-meta text-xs text-[hsl(var(--bk-accent))] bg-[hsl(var(--bk-accent))]/10 px-3 py-1 rounded mb-6">
              {category.name}
            </span>
          )}

          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-[-0.03em] text-[hsl(var(--bk-fg))] mb-6 max-w-4xl">
            {article.title}
          </h1>

          {article.excerpt && (
            <p className="font-body text-xl text-[hsl(var(--bk-fg-muted))] mb-8 max-w-3xl leading-relaxed">
              {article.excerpt}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-6 mb-8 font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))]">
            <span className="flex items-center gap-2"><User size={14} /> {article.author}</span>
            <span className="flex items-center gap-2">
              <Calendar size={14} />
              {new Date(article.published_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </span>
            <span className="flex items-center gap-2"><Clock size={14} /> {article.reading_time} min read</span>
          </div>

          {/* Social Sharing */}
          <div className="flex items-center gap-4 pb-8 border-b border-[hsl(var(--bk-line))]">
            <span className="font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))] flex items-center gap-2">
              <Share2 size={14} /> Share:
            </span>
            <a
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(shareUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[hsl(var(--bk-fg-muted))] hover:text-[hsl(var(--bk-accent))] transition-colors"
            >
              <Twitter size={18} />
            </a>
            <a
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[hsl(var(--bk-fg-muted))] hover:text-[hsl(var(--bk-accent))] transition-colors"
            >
              <Linkedin size={18} />
            </a>
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[hsl(var(--bk-fg-muted))] hover:text-[hsl(var(--bk-accent))] transition-colors"
            >
              <Facebook size={18} />
            </a>
          </div>
        </SectionReveal>
      </div>

      {/* Hero Image */}
      {article.hero_image && (
        <SectionReveal>
          <div className="max-w-[1400px] mx-auto px-5 sm:px-8 mb-16">
            <div className="aspect-[21/9] rounded-lg overflow-hidden">
              <img
                src={article.hero_image}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </SectionReveal>
      )}

      {/* Article Content */}
      <div className="max-w-[800px] mx-auto px-5 sm:px-8">
        <SectionReveal>
          <div 
            className="prose prose-invert prose-lg max-w-none
              prose-headings:font-display prose-headings:tracking-tight
              prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6
              prose-h3:text-2xl prose-h3:mt-10 prose-h3:mb-4
              prose-p:text-[hsl(var(--bk-fg-muted))] prose-p:leading-relaxed prose-p:mb-6
              prose-a:text-[hsl(var(--bk-accent))] prose-a:no-underline hover:prose-a:opacity-80
              prose-strong:text-[hsl(var(--bk-fg))] prose-strong:font-semibold
              prose-img:rounded-lg prose-img:my-12
              prose-blockquote:border-l-4 prose-blockquote:border-[hsl(var(--bk-accent))] prose-blockquote:pl-6 prose-blockquote:italic prose-blockquote:text-[hsl(var(--bk-fg))]
              prose-ul:my-6 prose-li:text-[hsl(var(--bk-fg-muted))]"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />
        </SectionReveal>

        {/* Tags */}
        {article.tags && article.tags.length > 0 && (
          <SectionReveal delay={0.2}>
            <div className="mt-12 pt-8 border-t border-[hsl(var(--bk-line))]">
              <p className="font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))] mb-4">Topics</p>
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag, index) => (
                  <span 
                    key={index}
                    className="inline-block font-mono-meta text-xs text-[hsl(var(--bk-fg))] bg-[hsl(var(--bk-bg-elev))] border border-[hsl(var(--bk-line))] px-3 py-1.5 rounded-full hover:border-[hsl(var(--bk-accent))] transition-colors"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </SectionReveal>
        )}
      </div>

      {/* Author Bio */}
      <div className="max-w-[800px] mx-auto px-5 sm:px-8 mt-16 pt-12 border-t border-[hsl(var(--bk-line))]">
        <SectionReveal>
          <div className="flex gap-6">
            <div className="flex-1">
              <p className="font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))] mb-2">Written by</p>
              <h3 className="font-display text-2xl text-[hsl(var(--bk-fg))] mb-3">{article.author}</h3>
              <p className="font-body text-sm text-[hsl(var(--bk-fg-muted))] leading-relaxed">
                {article.author === 'Paco Herrera' 
                  ? 'Director, Cinematographer & Founder of Baklight. Specializing in documentary, brand storytelling, and production services in Mexico.'
                  : 'Baklight is a director-led production ecosystem in Mexico. From original films to brand stories, production services, post-production and equipment.'}
              </p>
            </div>
          </div>
        </SectionReveal>
      </div>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 mt-20 pt-12 border-t border-[hsl(var(--bk-line))]">
          <SectionReveal>
            <h3 className="font-display text-2xl text-[hsl(var(--bk-fg))] mb-8">Related Articles</h3>
          </SectionReveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {relatedArticles.map((related, i) => (
              <SectionReveal key={related.id} delay={i * 0.1}>
                <Link to={`/journal/${related.slug}`} className="group block">
                  <div className="relative aspect-[4/3] rounded-lg overflow-hidden mb-4">
                    <img
                      src={related.hero_image}
                      alt={related.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <h4 className="font-display text-lg text-[hsl(var(--bk-fg))] mb-2 group-hover:text-[hsl(var(--bk-accent))] transition-colors">
                    {related.title}
                  </h4>
                  <p className="font-body text-sm text-[hsl(var(--bk-fg-muted))] line-clamp-2">
                    {related.excerpt}
                  </p>
                </Link>
              </SectionReveal>
            ))}
          </div>
        </div>
      )}

      {/* Contextual CTA */}
      {cta && (
        <div className="max-w-[800px] mx-auto px-5 sm:px-8 mt-20">
          <SectionReveal>
            <div className="bg-[hsl(var(--bk-bg-elev))] border border-[hsl(var(--bk-line))] rounded-lg p-8 md:p-12 text-center">
              <h3 className="font-display text-2xl md:text-3xl text-[hsl(var(--bk-fg))] mb-4">
                {cta.title}
              </h3>
              <p className="font-body text-[hsl(var(--bk-fg-muted))] mb-8 max-w-lg mx-auto">
                {cta.description}
              </p>
              <Link
                to={cta.link}
                className="inline-flex items-center gap-2 font-body text-sm px-8 py-3 bg-[hsl(var(--bk-accent))] text-[hsl(var(--bk-bg))] rounded-[var(--radius)] hover:opacity-90 transition-opacity"
              >
                {cta.linkText} <ArrowRight size={16} />
              </Link>
            </div>
          </SectionReveal>
        </div>
      )}
    </div>
  );
}
